import { useState } from 'react'
import { axiosInstance } from '../../helpers'

import { toast } from 'react-toastify'
import { insertOrUpdateIdentifiable } from '../services'
import { ApartmentDeadline, DeadlineDetail } from '../../models'

export const useDeadline = () => {
    const [apartmentDeadlineList, setApartmentDeadlineList] = useState<
        ApartmentDeadline[] | null
    >(null)
    const [loadingState, setLoadingState] = useState<LoadingState>({
        getList: false,
        getDetail: false,
        create: false,
        createBulk: false,
        transition: false,
        delete: false,
    })

    const getApartmentDeadlines = (errorHandler?: (e: any) => void) => {
        setLoadingState({ ...loadingState, getList: true })
        axiosInstance
            .get('deadline/')
            .then((res) => {
                setApartmentDeadlineList(res.data)
            })
            .catch((e) => {
                if (errorHandler) {
                    errorHandler(e)
                } else {
                    toast.error(`unable to load deadlines`)
                }
            })
            .finally(() => {
                setLoadingState({ ...loadingState, getList: false })
            })
    }

    const createDeadline = (request: CreateDeadlineRequest) => {
        setLoadingState({ ...loadingState, create: true })

        return new Promise<ApartmentDeadline>((resolve, reject) => {
            axiosInstance
                .post('deadline/', request)
                .then((res: any) => {
                    insertOrUpdateIdentifiable(
                        res.data,
                        setApartmentDeadlineList,
                    )

                    resolve(res.data)
                })
                .catch(reject)
                .finally(() => {
                    setLoadingState({ ...loadingState, getList: false })
                })
        })
    }

    const getDeadlineDetail = (deadlineId: number) => {
        setLoadingState({ ...loadingState, getDetail: true })

        return new Promise<DeadlineDetail>((resolve, reject) => {
            axiosInstance
                .get(`deadline/${deadlineId}/`)
                .then((res) => {
                    return resolve(res.data)
                })
                .catch((e) => {
                    reject(e)
                })
                .finally(() => {
                    setLoadingState({ ...loadingState, getDetail: false })
                })
        })
    }

    const transitionDeadline = (deadlineId: number, complete: boolean) => {
        setLoadingState({ ...loadingState, transition: true })

        return new Promise<DeadlineDetail>((resolve, reject) => {
            axiosInstance
                .post(`deadline/${deadlineId}/set_complete_status/`, {
                    complete_status: complete,
                })
                .then((res) => {
                    const deadlineDetail: DeadlineDetail = res.data
                    const aptDeadline = deadlineDetail.apartment_deadlines.find(
                        (aptDl) => aptDl.id === deadlineId,
                    )
                    if (aptDeadline) {
                        insertOrUpdateIdentifiable(
                            aptDeadline,
                            setApartmentDeadlineList,
                        )
                    }

                    return resolve(res.data)
                })
                .catch((e) => {
                    reject(e)
                })
                .finally(() => {
                    setLoadingState({ ...loadingState, transition: false })
                })
        })
    }

    const incrementMessageCount = (deadline: ApartmentDeadline) => {
        insertOrUpdateIdentifiable(
            { ...deadline, message_count: deadline.message_count + 1 },
            setApartmentDeadlineList,
        )
    }

    const deleteApartmentDeadline = (apartmentDeadlineId: number) => {
        return new Promise<void>((resolve, reject) => {
            setLoadingState({ ...loadingState, delete: true })
            axiosInstance
                .delete(`deadline/${apartmentDeadlineId}/`)
                .then((res) => {
                    if (apartmentDeadlineList) {
                        setApartmentDeadlineList(
                            apartmentDeadlineList?.filter(
                                (aptDl) => aptDl.id !== apartmentDeadlineId,
                            ) ?? null,
                        )
                    }

                    return resolve()
                })
                .catch((e) => {
                    reject(e)
                })
                .finally(() => {
                    setLoadingState({ ...loadingState, delete: false })
                })
        })
    }

    const addApartmentDeadlines = (request: AddApartmentDeadlineRequest) => {
        return new Promise<DeadlineDetail>((resolve, reject) => {
            axiosInstance
                .post('deadline/add_apartment_deadlines/', request)
                .then((res) => {
                    const deadlineDetail: DeadlineDetail = res.data
                    resolve(deadlineDetail)
                })
                .catch(reject)
        })
    }

    const createBulkDeadlines = (request: CreateBulkDeadlinesRequest) => {
        setLoadingState((prev) => ({ ...prev, createBulk: true }))

        const deadlines = request.deadlines
            .filter((d) => d.name && d.name.trim() !== '')
            .map((d) => {
                const item: {
                    name: string
                    description: string
                    date: string
                    reminder_date?: string | null
                } = {
                    name: d.name.trim(),
                    description: (d.description ?? '').trim(),
                    date: toDeadlineDateISO(d.date),
                }
                if (d.reminder_date) {
                    item.reminder_date = toDeadlineDateISO(d.reminder_date)
                }
                return item
            })

        const body = {
            deadlines,
            apartments: request.apartments,
        }

        return axiosInstance
            .post<ApartmentDeadline[]>('deadline/bulk/', body)
            .then((res) => {
                const created = res.data ?? []
                created.forEach((aptDeadline) =>
                    insertOrUpdateIdentifiable(
                        aptDeadline,
                        setApartmentDeadlineList,
                    ),
                )
                return created
            })
            .finally(() => {
                setLoadingState((prev) => ({ ...prev, createBulk: false }))
            })
    }

    return {
        apartmentDeadlineList,
        loadingState,
        getApartmentDeadlines,
        createDeadline,
        getDeadlineDetail,
        transitionDeadline,
        incrementMessageCount,
        deleteApartmentDeadline,
        addApartmentDeadlines,
        createBulkDeadlines,
    }
}

function toDeadlineDateISO(dateStr: string): string {
    if (!dateStr || !dateStr.trim()) return new Date().toISOString()
    const trimmed = dateStr.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return `${trimmed}T12:00:00.000Z`
    }
    return new Date(trimmed).toISOString()
}

export interface CreateDeadlineRequest {
    name: string
    description: string
    date: string
    apartments: number[]
    reminder_date?: string | null
}

interface LoadingState {
    getList: boolean
    getDetail: boolean
    create: boolean
    createBulk: boolean
    transition: boolean
    delete: boolean
}

export interface CreateBulkDeadlinesRequest {
    deadlines: Array<{
        date: string
        name: string
        description: string
        reminder_date?: string | null
    }>
    apartments: number[]
}

export interface AddApartmentDeadlineRequest {
    deadline_id: number
    apartment_ids: number[]
}
